import { computed, onMounted, onUnmounted, Ref, ref, watchEffect } from '@vue/composition-api';
import { RowEventContext } from 'tdesign-vue';
import { i18n_t } from '@/locales/i18n';
import {
  SyncTableItemData,
  SyncTableSortConfig,
} from '@/renderer/views/desktop/FileManagement/FileSync/composition/FileSyncUIDefine';
import { CoSyncCenterEvent } from '@/renderer/scripts/CoSync/SyncCenter/CoSyncCenterEvent';
import { SpaceTag } from '@/renderer/scripts/libs/smh-sdk-class/typings';
import {
  CoSyncStrategy,
  CoSyncTimingType,
  getName,
  GetRemotePathWithSpaceAndTeam,
} from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncConfig';
import SyncCenter, { SyncTaskUIStatus } from '@/renderer/scripts/CoSync/SyncCenter/SyncCenter';
import { syncCenterEventBus } from '@/renderer/scripts/CoSync/SyncCenter/SyncCenterEventBus';
import { coSync } from '@/renderer/scripts/CoSync/coSync';

export default function useCoSyncSourceHandler() {
  // 表格的排序方法
  type SortFunc = (lhs: SyncTableItemData, rhs: SyncTableItemData) => number;
  const buildSortFunc = (sortConfig: SyncTableSortConfig): SortFunc => {
    const { sortBy, descending } = sortConfig;
    return (lhs: SyncTableItemData, rhs: SyncTableItemData): number => {
      let lhsProp: any = undefined;
      let rhsProp: any = undefined;

      // 此处的字符串对应t-design的row的名称
      if (sortBy === 'taskName') {
        lhsProp = lhs.taskName;
        rhsProp = rhs.taskName;
      } else if (sortBy === 'status') {
        lhsProp = lhs.statusInfo.status;
        rhsProp = rhs.statusInfo.status;
      } else if (sortBy === 'remotePath') {
        lhsProp = lhs.remotePath;
        rhsProp = rhs.remotePath;
      } else {
        // sortBy === 'localPath'
        lhsProp = lhs.localPath;
        rhsProp = rhs.localPath;
      }

      if (lhsProp > rhsProp) {
        return descending ? -1 : 1;
      }
      if (lhsProp < rhsProp) {
        return descending ? 1 : -1;
      }
      return 0;
    };
  };

  // 表格数据的创建方法
  const buildSyncTableData = (status: SyncTaskUIStatus): SyncTableItemData => {
    const buildTimingText = (timingType: CoSyncTimingType): string => {
      switch (timingType) {
        case CoSyncTimingType.MANUL:
          return i18n_t('手动');
        case CoSyncTimingType.AUTO:
          return i18n_t('自动');
        case CoSyncTimingType.TIMIING:
          return i18n_t('定时');

        default:
          // eslint-disable-next-line no-case-declarations
          const _exhaustiveCheck: never = timingType;
          return _exhaustiveCheck;
      }
    };
    const buildSpaceIcon = (spaceTag: SpaceTag): string => {
      switch (spaceTag) {
        case SpaceTag.team:
          return 'cosync-enterprise-space';
        case SpaceTag.personal:
          return 'cosync-persoanl-space';

        default:
          return 'cosync-persoanl-space';
      }
    };

    const buildIcon = (strategy: CoSyncStrategy): string => {
      switch (strategy) {
        case CoSyncStrategy.BOTH:
          return 'twoway-sync-icon';

        case CoSyncStrategy.LOCAL_TO_CLOUD:
          return 'upload-sync-icon';

        case CoSyncStrategy.CLOUD_TO_LOCAL:
          return 'download-sync-icon';

        default: {
          const _exhaustiveCheck: never = strategy;
          return _exhaustiveCheck;
        }
      }
    };

    const { spaceTag, teamPath, remotePath } = status.coSyncInfo.config.syncPath;

    const item: SyncTableItemData = {
      taskId: status.coSyncInfo.localSyncId,
      timingTypeWording: buildTimingText(status.coSyncInfo.config.syncSetting.timingType),
      taskName: getName(status.coSyncInfo.config.syncPath),
      statusInfo: status.statusInfo,
      remotePath: status.coSyncInfo.config.syncPath.remotePath,
      remoteSpaceIcon: buildSpaceIcon(status.coSyncInfo.config.syncPath.spaceTag),
      remotePathWithSpaceAndTeam: GetRemotePathWithSpaceAndTeam(spaceTag, teamPath, remotePath),
      localPath: status.coSyncInfo.config.syncPath.localPath,
      coSyncInfo: status.coSyncInfo,
      strategyIcon: buildIcon(status.coSyncInfo.config.syncSetting.strategy),
      source: status,
    };

    return item;
  };

  // 内部控制hover的数据（为了兼容下拉按钮）
  // 实时hover的行
  const realtimeHoverRowData = ref<SyncTableItemData | null>(null);
  // 下拉按钮展开时hover的行
  const hoverRowDataWhenDropdown = ref<SyncTableItemData | null>(null);
  // 鼠标hover的行数据
  const hoverRawData = computed(() => {
    if (hoverRowDataWhenDropdown.value === null) return realtimeHoverRowData.value;
    return hoverRowDataWhenDropdown.value;
  });

  // 表格配置
  const tableColumns = [
    {
      colKey: 'row-select',
      type: 'multiple',
    },
    {
      colKey: 'taskName',
      title: i18n_t('项目名称'),
      sorter: true,
      ellipsis: true,
      width: '200',
    },
    {
      colKey: 'status',
      title: i18n_t('状态'),
      width: '200',
    },
    {
      colKey: 'remotePath',
      title: i18n_t('云端位置'),
      width: '200',
    },
    {
      colKey: 'localPath',
      title: i18n_t('本地位置'),
      width: 200, // 由于此栏承载了操作按钮组，因此宽度必须 > 130px
      fixed: 'right',
    },
  ];

  // 原始状态数据
  const taskStatusList: Ref<SyncTaskUIStatus[]> = ref([]);

  // 表格数据
  const tableData = computed<SyncTableItemData[]>(() => {
    // 生成表格数据
    const tableData = taskStatusList.value.map(buildSyncTableData);

    if (!sortConfig.value) {
      return tableData;
    }

    // 排序
    const sortFunc = buildSortFunc(sortConfig.value);
    return tableData.sort(sortFunc);
  });

  // 选中的行(内部使用)
  const innerSelectedRows = ref<SyncTableItemData[]>([]);
  // 选中的行
  const selectedRows = computed<SyncTableItemData[]>(() =>
    tableData.value.filter(it => innerSelectedRows.value.find(selected => selected.taskId === it.taskId)),
  );

  // 选中的taskId
  const selectedRowKeys = computed<number[]>(() => selectedRows.value.map(it => it.taskId));

  // 排序的配置
  const sortConfig = ref<SyncTableSortConfig | undefined>({
    // 按照 status 字段进行排序
    sortBy: 'taskName',
    // 是否按照降序进行排序
    descending: false,
  });

  // 鼠标hover的任务id
  const hoverRowTaskId = computed<number>(() => hoverRawData.value?.taskId ?? -1);

  const methods = {
    rehandleSelectChange(selectedDataList: SyncTableItemData[]) {
      innerSelectedRows.value = selectedDataList;
    },

    sortChange(sort: SyncTableSortConfig | undefined) {
      sortConfig.value = sort;
    },

    handleMouseDown(context: RowEventContext<any>) {
      const { index, row, e } = context;
      const { target, button, type } = e;
      if (type === 'mousedown' && button === 2) {
        methods.handleClickRow(context);
      }
    },

    handleClickRow(context: RowEventContext<any>) {
      const { index, row, e } = context;
      const clickRow = row as SyncTableItemData;
      const { target, button, type, ctrlKey, shiftKey, metaKey } = e;
      const { className, parentElement } = target as HTMLElement;

      if (button === 0) {
        if (target && typeof className === 'string' && className.includes('t-checkbox')) return;
        innerSelectedRows.value = [clickRow];
      } else if (button === 2) {
        if (!innerSelectedRows.value.find(it => it.coSyncInfo.localSyncId === clickRow.coSyncInfo.localSyncId)) {
          innerSelectedRows.value = [clickRow];
        }
      }
    },

    handleEnterRow(context: RowEventContext<any>) {
      const { index, row, e } = context;
      realtimeHoverRowData.value = row;
    },

    handleLeaveRow(context: RowEventContext<any>) {
      realtimeHoverRowData.value = null;
    },

    handleDropdownClick(row: SyncTableItemData) {
      hoverRowDataWhenDropdown.value = row;
    },

    handleDropdownClickOutside() {
      hoverRowDataWhenDropdown.value = null;
    },
  };

  const onTaskStatusMapChange = (param?: Map<number, SyncTaskUIStatus>) => {
    let statusMap = param;
    if (!statusMap) {
      statusMap = new Map<number, SyncTaskUIStatus>();
    }
    taskStatusList.value = Array.from(statusMap.values());
  };

  onMounted(() => {
    onTaskStatusMapChange(coSync.centerData().syncTaskStatusMap);
    syncCenterEventBus.on(CoSyncCenterEvent.onTaskStatusMapChange, onTaskStatusMapChange);
  });

  onUnmounted(() => {
    syncCenterEventBus.off(CoSyncCenterEvent.onTaskStatusMapChange, onTaskStatusMapChange);
  });

  return {
    tableColumns,
    tableData,
    selectedRows,
    selectedRowKeys,
    sortConfig,
    hoverRowTaskId,

    methods,
  };
}
